import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatBRL } from "@/lib/constants";
import { CheckCircle2, Loader2, Crown, CreditCard, XCircle, Info } from "lucide-react";

const ORDER = ["free", "basic", "professional", "enterprise", "anual_pro"];
const FEATS = {
  free: ["5 documentos", "10 clientes", "1 usuário", "PDF profissional", "Assinatura digital"],
  basic: ["50 documentos", "200 clientes", "Orçamentos e O.S.", "Fotos nos documentos", "Compartilhar no WhatsApp"],
  professional: ["500 documentos", "2.000 clientes", "Assistente IA (descrições e diagnóstico)", "Laudos técnicos", "Até 3 usuários", "Cobrança automática (WhatsApp/E-mail)", "Recibos com QR Code", "Relatórios financeiros (PDF/CSV)"],
  enterprise: ["Documentos ilimitados", "Clientes ilimitados", "Assistente IA", "Equipe com permissões (admin, gerente, técnico)", "Contratos de manutenção recorrente", "Até 20 usuários", "Suporte prioritário"],
  anual_pro: ["Tudo ilimitado", "Todos os tipos de documento", "Assistente IA", "Laudos técnicos", "Até 5 usuários", "Relatórios avançados", "12 meses de acesso"],
};

export default function Plans() {
  const { user, checkAuth } = useAuth();
  const [plans, setPlans] = useState(null);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState("");
  const [billing, setBilling] = useState("monthly");

  const load = () => Promise.all([api.get("/plans"), api.get("/subscription")]).then(([p, s]) => { setPlans(p.data); setSub(s.data); });
  useEffect(() => {
    (async () => {
      try { await api.post("/subscription/sync"); } catch (e) { console.error("subscription sync failed", e); }
      try {
        const { data } = await api.post("/subscription/confirm");
        if (data?.activated) { await checkAuth(); toast.success("Pagamento confirmado! Plano ativado 🎉"); }
      } catch (e) { console.error("subscription confirm failed", e); }
      load();
    })();
  }, []);

  const choose = async (plan) => {
    setBusy(plan);
    try {
      if (plan === "free") {
        await api.post("/subscription", { plan });
        await checkAuth();
        toast.success("Plano alterado");
        load();
      } else {
        const b = plan === "anual_pro" ? "annual" : billing;
        const { data } = await api.post("/subscription/checkout", { plan, billing: b, back_url: window.location.origin + "/plans" });
        if (data.init_point) window.location.assign(data.init_point);
        else toast.error("Não foi possível iniciar o checkout");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); } finally { setBusy(""); }
  };

  const cancel = async () => { setBusy("cancel"); try { await api.post("/subscription/cancel"); await checkAuth(); toast.success("Assinatura cancelada"); load(); } finally { setBusy(""); } };

  if (!plans || !sub) return <div className="h-64 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  const current = sub.plan;

  return (
    <div className="space-y-5">
      <div><h1 className="font-display font-extrabold text-2xl sm:text-3xl">Minha Assinatura</h1><p className="text-muted-foreground">Gerencie seu plano</p></div>

      <Card className="p-5 bg-primary/5 border-primary/20">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm text-muted-foreground">Plano atual</p>
            <p className="font-display font-extrabold text-2xl flex items-center gap-2"><Crown className="h-6 w-6 text-primary" /> {sub.is_trial ? "Período de teste" : (plans[current]?.label || "Gratuito")}</p>
            <div className="text-sm text-muted-foreground mt-1 flex items-center flex-wrap gap-1">
              {sub.is_trial ? `Teste — ${sub.trial_days_left} dia(s) restante(s)` : (plans[current]?.price ? `${formatBRL(plans[current].price)}/mês` : "Grátis")} • Status: <Badge variant="outline" className="ml-1">{sub.status}</Badge>
            </div>
            {sub.next_payment && <p className="text-sm text-muted-foreground">Próxima cobrança: {sub.next_payment}</p>}
          </div>
          {sub.status === "active" && current !== "free" && current !== "trial" && <Button variant="outline" onClick={cancel} disabled={busy === "cancel"} data-testid="cancel-sub-btn"><XCircle className="h-4 w-4 mr-1" /> Cancelar assinatura</Button>}
        </div>
      </Card>

      <div className="rounded-xl bg-sky-50 dark:bg-sky-500/10 border border-sky-200 dark:border-sky-500/30 p-4 flex gap-2 text-sm text-sky-800 dark:text-sky-300">
        <Info className="h-5 w-5 shrink-0" />
        <p>Pagamentos processados pelo <b>Mercado Pago</b>. Ao assinar, você será redirecionado para o checkout seguro e poderá pagar com <b>Pix, cartão de crédito ou débito</b>. O plano é ativado automaticamente após a confirmação.</p>
      </div>

      <div className="flex justify-center">
        <div className="inline-flex rounded-xl border border-border bg-card p-1" data-testid="billing-toggle">
          <button onClick={() => setBilling("monthly")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${billing === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="billing-monthly">Mensal</button>
          <button onClick={() => setBilling("annual")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${billing === "annual" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="billing-annual">Anual</button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        {ORDER.map((id) => {
          const p = plans[id];
          const isCurrent = current === id;
          const popular = id === "professional";
          const annualOnly = !!p.annual_only;
          const priceLabel = annualOnly ? formatBRL(p.price_annual) : (p.price === 0 ? "Grátis" : formatBRL(billing === "annual" ? p.price_annual : p.price));
          const per = annualOnly ? "/ano" : (p.price > 0 ? (billing === "annual" ? "/ano" : "/mês") : "");
          return (
            <Card key={id} className={`p-5 relative flex flex-col ${popular ? "border-primary ring-2 ring-primary/30" : ""} ${annualOnly ? "border-secondary ring-2 ring-secondary/30" : ""}`} data-testid={`plan-card-${id}`}>
              {popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">POPULAR</span>}
              {annualOnly && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground text-xs font-bold px-3 py-1 rounded-full">MELHOR VALOR</span>}
              <h3 className="font-display font-bold text-xl">{p.label}</h3>
              <p className="mt-2"><span className="text-3xl font-display font-extrabold">{priceLabel}</span>{per && <span className="text-muted-foreground text-sm">{per}</span>}</p>
              <ul className="mt-4 space-y-2 flex-1">
                {FEATS[id].map((f) => <li key={f} className="flex items-start gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-secondary shrink-0 mt-0.5" />{f}</li>)}
              </ul>
              <Button className="w-full mt-5" variant={isCurrent ? "outline" : popular ? "default" : "outline"} disabled={isCurrent || busy === id} onClick={() => choose(id)} data-testid={`choose-plan-${id}`}>
                {busy === id ? <Loader2 className="h-4 w-4 animate-spin" /> : isCurrent ? "Plano atual" : <><CreditCard className="h-4 w-4 mr-1" /> {id === "free" ? "Selecionar" : "Assinar"}</>}
              </Button>
            </Card>
          );
        })}
      </div>

      {sub.history?.length > 0 && (
        <Card className="p-5">
          <h2 className="font-display font-bold text-lg mb-3">Histórico de pagamentos</h2>
          <div className="space-y-2">
            {sub.history.slice().reverse().map((h) => (
              <div key={h.at} className="flex justify-between text-sm border-b border-border pb-2"><span>{plans[h.plan]?.label}</span><span className="text-muted-foreground">{new Date(h.at).toLocaleDateString("pt-BR")}</span><span className="font-semibold">{formatBRL(h.amount)}</span></div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
